import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Phone, Instagram, Facebook, Globe, BadgeCheck, Clock, Navigation, Share2, Star, AlertCircle, Calendar, Heart, Bell, Send, Stamp, Gift } from "lucide-react";
import { api, apiError, mediaUrl } from "../lib/api";
import { cuisineStyle } from "../lib/cuisines";
import { MapView } from "../components/MapView";
import { useFavorites } from "../hooks/useFavorites";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Seo } from "../components/Seo";
import { toast } from "sonner";

const DAY_LABELS = { monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi", thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi", sunday: "Dimanche" };

export default function TruckDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [err, setErr] = useState(false);
  const [active, setActive] = useState(0);
  const [rForm, setRForm] = useState({ name: "", rating: 5, comment: "" });
  const [subEmail, setSubEmail] = useState("");
  const { isFav, toggle } = useFavorites();
  const { user } = useAuth();
  const [loyalty, setLoyalty] = useState({ stamps: 0, progress: 0, threshold: 10, rewards: 0, stamped_today: false });

  useEffect(() => { api.get(`/trucks/${slug}`).then((r) => setT(r.data)).catch(() => setErr(true)); }, [slug]);
  useEffect(() => {
    let src = "direct";
    try {
      const ref = document.referrer;
      if (ref) {
        const u = new URL(ref);
        if (u.host === window.location.host) {
          if (u.pathname.startsWith("/recherche")) src = "recherche";
          else if (u.pathname === "/") src = "accueil";
          else if (u.pathname.startsWith("/favoris")) src = "favoris";
          else src = "interne";
        } else src = "externe";
      }
    } catch { /* noop */ }
    api.post(`/track/${slug}`, { type: "view", source: src }).catch(() => {});
  }, [slug]);

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/trucks/${slug}/reviews`, { ...rForm, rating: Number(rForm.rating) });
      toast.success("Merci pour votre avis !");
      setRForm({ name: "", rating: 5, comment: "" });
      const r = await api.get(`/trucks/${slug}`); setT(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const doSubscribe = async (e) => {
    e.preventDefault();
    try { await api.post(`/trucks/${slug}/subscribe`, { email: subEmail }); toast.success("C'est noté ! Vous serez alerté à chaque changement d'emplacement."); setSubEmail(""); }
    catch (e) { toast.error(apiError(e)); }
  };
  const stampVisit = async () => {
    try { const { data } = await api.post(`/loyalty/${slug}/stamp`); setLoyalty(data); toast.success("Tampon ajouté ! Merci de votre visite."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const redeemReward = async () => {
    try { const { data } = await api.post(`/loyalty/${slug}/redeem`); setLoyalty(data); toast.success("Récompense validée ! À présenter au gérant."); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { if (user) api.get(`/loyalty/${slug}`).then((r) => setLoyalty(r.data)).catch(() => {}); }, [user, slug]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><h1 className="font-display font-bold text-2xl text-urban-teal">Foodtruck introuvable</h1><Button onClick={() => nav("/recherche")} className="rounded-full bg-street-gold text-white font-bold mt-5">Retour à la recherche</Button></div>;
  if (!t) return <p className="text-center py-24 text-urban-teal/50">Chargement...</p>;

  const { Icon, bg, fg } = cuisineStyle(t.cuisine);
  const photos = t.photos || [];
  const premium = t.plan === "pro" || t.plan === "proplus";
  const unmanaged = !t.managed;

  const share = () => { navigator.clipboard.writeText(window.location.href); toast.success("Lien copié !"); api.post(`/track/${slug}`, { type: "share" }).catch(() => {}); };
  const itineraire = () => {
    const q = encodeURIComponent(t.today_location?.address || `${t.name} ${t.city}`);
    api.post(`/track/${slug}`, { type: "itinerary" }).catch(() => {});
    window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, "_blank");
  };
  const report = async () => {
    try { await api.post(`/trucks/${slug}/report`, { slug, message: "Signalé depuis la fiche publique" }); toast.success("Merci, le gérant sera invité à récupérer sa page."); }
    catch (e) { toast.error(apiError(e)); }
  };

  const photoUrls = photos.map((p) => mediaUrl(p));
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    name: t.name,
    ...(t.description ? { description: t.description } : {}),
    ...(t.cuisine ? { servesCuisine: t.cuisine } : {}),
    ...(photoUrls.length ? { image: photoUrls } : {}),
    ...(t.phone ? { telephone: t.phone } : {}),
    priceRange: "€-€€",
    address: { "@type": "PostalAddress", addressLocality: t.city || "", addressCountry: "FR" },
    ...(t.lat && t.lng ? { geo: { "@type": "GeoCoordinates", latitude: t.lat, longitude: t.lng } } : {}),
    ...(t.rating_count ? { aggregateRating: { "@type": "AggregateRating", ratingValue: t.rating_avg, reviewCount: t.rating_count } } : {}),
    url: `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/foodtruck/${slug}`,
  };
  const seoDesc = t.description
    ? t.description.slice(0, 160)
    : `${t.name}, foodtruck ${t.cuisine || ""} à ${t.city || ""}. Découvrez son emplacement du jour, ses horaires et les avis clients sur StreatEat.`;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-28 lg:pb-8">
      <Seo
        title={`${t.name}${t.city ? " - " + t.city : ""}`}
        description={seoDesc}
        path={`/foodtruck/${slug}`}
        type="restaurant"
        image={photoUrls[0]}
        jsonLd={jsonLd}
      />
      {/* Gallery */}
      <div className="rounded-3xl overflow-hidden shadow-warm bg-white">
        <div className="h-64 sm:h-80 relative">
          {photos.length ? (
            <img src={mediaUrl(photos[active])} alt={t.name} className="w-full h-full object-cover" data-testid="truck-hero-photo" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: bg }}><Icon size={90} strokeWidth={1.5} style={{ color: fg }} /></div>
          )}
          <div className="absolute top-4 left-4 flex gap-2">
            {premium && <span className="px-3 py-1 rounded-full bg-street-gold text-white text-xs font-bold flex items-center gap-1"><Star size={12} className="fill-white" /> Premium</span>}
            {t.is_open ? <span className="px-3 py-1 rounded-full bg-fresh-mint text-white text-xs font-bold">Ouvert</span> : <span className="px-3 py-1 rounded-full bg-coral-twist text-white text-xs font-bold">Fermé</span>}
            {t.verified && <span className="px-3 py-1 rounded-full bg-urban-teal text-white text-xs font-bold flex items-center gap-1"><BadgeCheck size={12} /> Vérifié</span>}
          </div>
        </div>
        {photos.length > 1 && (
          <div className="flex gap-2 p-3 overflow-x-auto no-scrollbar">
            {photos.map((p, i) => <img key={p} onClick={() => setActive(i)} src={mediaUrl(p)} alt="" className={`h-16 w-20 object-cover rounded-lg cursor-pointer ${i === active ? "ring-2 ring-street-gold" : ""}`} />)}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2">
          <span className="text-xs font-bold uppercase tracking-wide" style={{ color: fg }}>{t.cuisine}</span>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold text-urban-teal mt-1">{t.name}</h1>
          <p className="text-urban-teal/60 flex items-center gap-1 mt-1"><MapPin size={16} /> {t.city}</p>
          {t.rating_count > 0 && (
            <div className="flex items-center gap-1.5 mt-2" data-testid="detail-rating">
              <div className="flex">{[1,2,3,4,5].map((n) => <Star key={n} size={16} className={n <= Math.round(t.rating_avg) ? "fill-street-gold text-street-gold" : "text-urban-teal/20"} />)}</div>
              <span className="font-bold text-urban-teal">{t.rating_avg}</span>
              <span className="text-urban-teal/50 text-sm">({t.rating_count} avis)</span>
            </div>
          )}
          {t.description && <p className="text-slate-600 mt-4 leading-relaxed">{t.description}</p>}

          {unmanaged && (
            <div className="bg-coral-twist/10 border border-coral-twist/30 rounded-2xl p-5 mt-6" data-testid="unmanaged-banner">
              <p className="font-display font-bold text-urban-teal flex items-center gap-2"><AlertCircle size={18} className="text-coral-twist" /> Cette page n'est pas encore gérée</p>
              <p className="text-sm text-urban-teal/70 mt-1">Ce foodtruck a été repéré mais son gérant ne l'a pas encore rejoint.</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button onClick={() => nav("/connexion?tab=signup")} className="rounded-full bg-street-gold text-white font-bold" data-testid="claim-btn">C'est mon foodtruck → Je réclame ma page</Button>
                <Button onClick={report} variant="ghost" className="rounded-full text-urban-teal font-semibold" data-testid="report-btn">Le signaler à son propriétaire</Button>
              </div>
            </div>
          )}

          {/* Today location */}
          {t.today_location?.label && (
            <div className="bg-white rounded-2xl shadow-warm p-5 mt-6">
              <p className="text-xs font-bold uppercase text-street-gold">Emplacement du jour</p>
              <p className="font-display font-bold text-xl text-urban-teal mt-1">{t.today_location.label}</p>
              {t.today_location.address && <p className="text-urban-teal/60 text-sm">{t.today_location.address}</p>}
              <Button onClick={itineraire} className="rounded-full bg-urban-teal text-white font-bold mt-3 gap-1" data-testid="directions-btn"><Navigation size={15} /> Itinéraire</Button>
            </div>
          )}

          {t.lat != null && t.lng != null && (
            <div className="mt-4" data-testid="detail-map"><MapView trucks={[t]} center={[t.lat, t.lng]} height={260} /></div>
          )}

          {/* Schedule */}
          {t.schedule?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-warm p-5 mt-4">
              <p className="font-display font-bold text-urban-teal flex items-center gap-2 mb-3"><Calendar size={17} /> Planning de la semaine</p>
              <div className="space-y-2">
                {t.schedule.map((s, i) => (
                  <div key={`${s.day}-${s.label}-${i}`} className="flex justify-between text-sm border-b border-cream-bg pb-2">
                    <span className="font-semibold text-urban-teal">{s.day}</span>
                    <span className="text-urban-teal/60">{s.label}{s.address ? ` · ${s.address}` : ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          <div className="bg-white rounded-2xl shadow-warm p-5 mt-4" data-testid="reviews-section">
            <p className="font-display font-bold text-urban-teal flex items-center gap-2 mb-3"><Star size={17} className="fill-street-gold text-street-gold" /> Avis clients {t.rating_count > 0 && <span className="text-urban-teal/50 text-sm">· {t.rating_avg}/5 ({t.rating_count})</span>}</p>
            {(!t.reviews || t.reviews.length === 0) && <p className="text-sm text-urban-teal/50">Aucun avis pour le moment. Soyez le premier !</p>}
            <div className="space-y-3">
              {(t.reviews || []).map((r) => (
                <div key={r.id} className="border-b border-cream-bg pb-3 last:border-0">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-urban-teal">{r.name}</span>
                    <div className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={13} className={n <= r.rating ? "fill-street-gold text-street-gold" : "text-urban-teal/20"} />)}</div>
                  </div>
                  {r.comment && <p className="text-sm text-slate-600 mt-1">{r.comment}</p>}
                  {r.owner_reply && (
                    <div className="bg-fresh-mint/10 rounded-xl p-2.5 mt-2 text-sm" data-testid={`review-reply-${r.id}`}>
                      <span className="font-bold text-fresh-mint">Réponse du gérant : </span>
                      <span className="text-urban-teal/80">{r.owner_reply}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <form onSubmit={submitReview} className="mt-4 bg-cream-bg rounded-2xl p-4">
              <p className="font-bold text-urban-teal text-sm mb-2">Laisser un avis</p>
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setRForm({ ...rForm, rating: n })} data-testid={`review-star-${n}`}><Star size={24} className={n <= rForm.rating ? "fill-street-gold text-street-gold" : "text-urban-teal/25"} /></button>
                ))}
              </div>
              <input required value={rForm.name} onChange={(e) => setRForm({ ...rForm, name: e.target.value })} placeholder="Votre prénom" className="w-full rounded-xl border-2 border-white bg-white px-3 py-2 text-sm outline-none mb-2 focus:border-street-gold" data-testid="review-name" />
              <textarea value={rForm.comment} onChange={(e) => setRForm({ ...rForm, comment: e.target.value })} rows={2} placeholder="Votre avis (optionnel)" className="w-full rounded-xl border-2 border-white bg-white px-3 py-2 text-sm outline-none focus:border-street-gold" data-testid="review-comment" />
              <Button type="submit" className="rounded-full bg-street-gold hover:bg-street-gold/90 text-white font-bold mt-2" data-testid="review-submit">Publier mon avis</Button>
            </form>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Button onClick={share} variant="outline" className="w-full rounded-full border-urban-teal/20 text-urban-teal font-bold gap-2" data-testid="share-btn"><Share2 size={16} /> Partager la fiche</Button>
          <Button onClick={() => toggle(slug)} variant="outline" className={`w-full rounded-full font-bold gap-2 ${isFav(slug) ? "border-coral-twist text-coral-twist bg-coral-twist/5" : "border-urban-teal/20 text-urban-teal"}`} data-testid="detail-fav-btn"><Heart size={16} className={isFav(slug) ? "fill-coral-twist" : ""} /> {isFav(slug) ? "Dans vos favoris" : "Ajouter aux favoris"}</Button>
          <form onSubmit={doSubscribe} className="bg-fresh-mint/10 rounded-2xl p-4" data-testid="subscribe-form">
            <p className="font-display font-bold text-urban-teal flex items-center gap-2"><Bell size={16} className="text-fresh-mint" /> Alertes emplacement</p>
            <p className="text-xs text-urban-teal/60 mt-1">Prévenu par e-mail dès qu'il change de lieu.</p>
            <div className="flex gap-2 mt-3">
              <input type="email" required value={subEmail} onChange={(e) => setSubEmail(e.target.value)} placeholder="votre@email.fr" className="flex-1 min-w-0 rounded-full border-2 border-white bg-white px-3 py-2 text-sm outline-none" data-testid="subscribe-email" />
              <Button type="submit" className="rounded-full bg-fresh-mint hover:bg-fresh-mint/90 text-white font-bold px-3 shrink-0" data-testid="subscribe-btn"><Send size={15} /></Button>
            </div>
          </form>
          {user && t.plan === "proplus" && (
            <div className="bg-white rounded-2xl shadow-warm p-5" data-testid="loyalty-card">
              <p className="font-display font-bold text-urban-teal flex items-center gap-2"><Stamp size={17} className="text-street-gold" /> Carte de fidélité</p>
              <p className="text-xs text-urban-teal/60 mt-1">{loyalty.threshold} visites = 1 offert{loyalty.rewards > 0 ? ` · ${loyalty.rewards} récompense(s) gagnée(s) !` : ""}</p>
              <div className="grid grid-cols-5 gap-2 my-3">
                {Array.from({ length: loyalty.threshold }).map((_, i) => (
                  <div key={i} className={`aspect-square rounded-full border-2 flex items-center justify-center ${i < loyalty.progress ? "bg-street-gold border-street-gold text-white" : "border-cream-bg text-urban-teal/20"}`}><Stamp size={13} /></div>
                ))}
              </div>
              <Button onClick={stampVisit} disabled={loyalty.stamped_today} className="w-full rounded-full bg-fresh-mint hover:bg-fresh-mint/90 text-white font-bold disabled:opacity-60" data-testid="stamp-btn">{loyalty.stamped_today ? "Visite validée aujourd'hui" : "Valider ma visite"}</Button>
              {loyalty.reward && <p className="text-xs text-urban-teal/70 mt-2 flex items-center gap-1"><Gift size={13} className="text-coral-twist" /> Récompense : {loyalty.reward}</p>}
              {loyalty.redeemable > 0 && <Button onClick={redeemReward} className="w-full rounded-full bg-coral-twist hover:bg-coral-twist/90 text-white font-bold mt-2" data-testid="redeem-btn">Utiliser ma récompense ({loyalty.redeemable})</Button>}
            </div>
          )}
          <div className="bg-white rounded-2xl shadow-warm p-5">
            <p className="font-display font-bold text-urban-teal mb-3">Infos pratiques</p>
            {t.hours && Object.keys(t.hours).length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-bold uppercase text-urban-teal/50 flex items-center gap-1 mb-1"><Clock size={13} /> Horaires</p>
                {Object.entries(t.hours).map(([d, ranges]) => (
                  <div key={d} className="flex justify-between text-sm text-urban-teal/70"><span>{DAY_LABELS[d] || d}</span><span>{ranges.map((r) => `${r.open}-${r.close}`).join(", ")}</span></div>
                ))}
              </div>
            )}
            <div className="space-y-2 text-sm">
              {t.phone && <a href={`tel:${t.phone}`} className="flex items-center gap-2 text-urban-teal hover:text-street-gold"><Phone size={15} /> {t.phone}</a>}
              {t.instagram && <a href={`https://instagram.com/${t.instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-urban-teal hover:text-street-gold"><Instagram size={15} /> @{t.instagram}</a>}
              {t.facebook && <a href={t.facebook} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-urban-teal hover:text-street-gold"><Facebook size={15} /> Facebook</a>}
              {t.website && <a href={t.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-urban-teal hover:text-street-gold"><Globe size={15} /> Site web</a>}
              {!t.phone && !t.instagram && !t.facebook && !t.website && <p className="text-urban-teal/40">Aucune coordonnée renseignée.</p>}
            </div>
          </div>
          <Link to="/recherche" className="block text-center text-street-gold font-bold text-sm">← Retour à la recherche</Link>
        </div>
      </div>

      {/* Sticky mobile action bar - accès permanent à l'itinéraire */}
      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-white/95 backdrop-blur border-t border-cream-bg px-3 py-2.5 pb-[calc(0.625rem+env(safe-area-inset-bottom))] flex items-center gap-2" data-testid="sticky-action-bar">
        <Button onClick={itineraire} className="flex-1 rounded-full bg-street-gold hover:bg-street-gold/90 text-white font-bold gap-1.5 h-11" data-testid="sticky-directions-btn"><Navigation size={17} /> Itinéraire</Button>
        {t.phone && (
          <a href={`tel:${t.phone}`} className="inline-flex items-center justify-center rounded-full border-2 border-fresh-mint text-fresh-mint h-11 w-11 shrink-0 active:scale-95 transition-transform" data-testid="sticky-call-btn" aria-label="Appeler"><Phone size={18} /></a>
        )}
        <button onClick={() => toggle(slug)} className={`inline-flex items-center justify-center rounded-full border-2 h-11 w-11 shrink-0 active:scale-95 transition-transform ${isFav(slug) ? "border-coral-twist text-coral-twist bg-coral-twist/5" : "border-urban-teal/20 text-urban-teal"}`} data-testid="sticky-fav-btn" aria-label="Favoris"><Heart size={18} className={isFav(slug) ? "fill-coral-twist" : ""} /></button>
      </div>
    </div>
  );
}
